import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { num, money, formatApiError } from "@/lib/api";
import { Card, PageHeader, Btn, Modal, Input, Select, StatusBadge, EmptyState, Th, Td } from "@/components/admin/ui";

function Progress({ label, actual, target }) {
  const pct = target ? Math.min(100, Math.round((actual / target) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{label}</span><span className="font-mono-metric text-slate-400">{num(actual)} / {num(target)}</span></div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#1D4ED8] rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", client: "", description: "", status: "Active", revenue_target: 2500000, leads: 10000, meetings: 1000, sales: 250 });

  const load = useCallback(() => { api.get("/campaigns").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/campaigns", { name: form.name, client: form.client, description: form.description, status: form.status, revenue_target: Number(form.revenue_target), kpi_targets: { leads: Number(form.leads), meetings: Number(form.meetings), sales: Number(form.sales) } });
      toast.success("Campaign created"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = async (cid) => { const { data } = await api.get(`/campaigns/${cid}`); setDetail(data); };

  return (
    <div className="space-y-6">
      <PageHeader title="Campaigns" subtitle={`${num(items.length)} campaigns`} action={<Btn data-testid="create-campaign-btn" onClick={() => setOpen(true)}>+ Create Campaign</Btn>} />
      {items.length === 0 ? <Card><EmptyState title="No campaigns yet" desc="Create your first campaign to begin assigning work." action={<Btn onClick={() => setOpen(true)}>+ Create Campaign</Btn>} /></Card> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((c) => (
            <Card key={c.id} className="p-5 cursor-pointer hover:border-white/20" onClick={() => openDetail(c.campaign_id)} data-testid={`campaign-card-${c.campaign_id}`}>
              <div className="flex items-start justify-between">
                <div><div className="font-head font-semibold text-white">{c.name}</div><div className="text-xs text-slate-500 mt-0.5">{c.client} · {c.campaign_id}</div></div>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-sm text-slate-400 mt-3 line-clamp-2">{c.description}</p>
              <div className="mt-4 text-xs text-[#1D4ED8] font-semibold">View performance →</div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create Campaign">
        <form onSubmit={create} className="space-y-3">
          <Input label="Campaign name" data-testid="campaign-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
            <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {["Draft", "Scheduled", "Active", "Paused", "Completed", "Archived"].map((s) => <option key={s}>{s}</option>)}
            </Select>
          </div>
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Revenue target" type="number" value={form.revenue_target} onChange={(e) => setForm({ ...form, revenue_target: e.target.value })} />
            <Input label="Leads target" type="number" value={form.leads} onChange={(e) => setForm({ ...form, leads: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Meetings target" type="number" value={form.meetings} onChange={(e) => setForm({ ...form, meetings: e.target.value })} />
            <Input label="Sales target" type="number" value={form.sales} onChange={(e) => setForm({ ...form, sales: e.target.value })} />
          </div>
          <Btn data-testid="campaign-submit" className="w-full">Create Campaign</Btn>
        </form>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.campaign?.name || ""} wide>
        {detail && (
          <div className="space-y-5">
            <div className="flex items-center gap-3"><StatusBadge status={detail.campaign.status} /><span className="text-slate-400 text-sm">{detail.campaign.campaign_id} · {detail.campaign.client}</span></div>
            <div className="space-y-4">
              <Progress label="Leads" actual={detail.actual.leads} target={detail.targets.leads || 0} />
              <Progress label="Meetings" actual={detail.actual.meetings} target={detail.targets.meetings || 0} />
              <Progress label="Sales" actual={detail.actual.sales} target={detail.targets.sales || 0} />
              <Progress label="Tasks completed" actual={detail.actual.tasks_completed} target={detail.actual.tasks || 0} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Revenue actual</div><div className="font-mono-metric text-xl font-bold text-emerald-400 mt-1">{money(detail.actual.revenue)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Revenue target</div><div className="font-mono-metric text-xl font-bold text-white mt-1">{money(detail.targets.revenue)}</div></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
